import { useEffect, useRef, useState } from "react"
import {
  FullscreenCloseButton,
  YourGoalContent,
  YourGoalWrapper,
} from "./styled"
import { Link } from "react-router-dom"
import PATHS from "router/paths"
import Button from "components/Buttons/Button"
import { useSetPageTitle } from "libs/ga"
import { getDisplayWeightWithUnit } from "utils/weightConverter"
import { useGoal } from "features/onboarding/hooks"
import WeightChangedNotification from "./WeightChangedNotification"
import { useBodyDisplayUnits } from "features/profile/hooks/body"
import MeshViewer from "components/MeshViewer"
import FixCtaBox from "components/Common/FixCtaBox"
import YourDailyTarget from "components/onboarding/Goal/YourDailyTarget"
import FullPageLoading from "components/Layouts/FullPageLoading"
import WeightTransition from "components/onboarding/Goal/WeightTransition"
import CloseWhiteIcon from "components/Icons/CloseWhiteIcon"

const YourGoal = () => {
  useSetPageTitle("Your Goal | Profile")
  const { goal } = useGoal()
  const { bodyDisplayUnits } = useBodyDisplayUnits()
  const [isFullscreen, setIsFullScreen] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const displayedStartWeight = getDisplayWeightWithUnit(
    bodyDisplayUnits,
    goal?.weight,
  )

  let displayedTargetWeight = getDisplayWeightWithUnit(
    bodyDisplayUnits,
    goal?.targetWeight ?? 0,
  )

  useEffect(() => {
    if (isFullscreen && containerRef.current) {
      containerRef?.current.scroll({
        top: 0,
        behavior: "smooth",
      })
    }
  }, [isFullscreen])

  if (!goal) {
    return <FullPageLoading />
  }

  return (
    <YourGoalWrapper
      ref={containerRef}
      style={
        {
          "--profile-header-height": "0px",
          overflow: isFullscreen ? "hidden" : "auto",
        } as React.CSSProperties
      }
    >
      {isFullscreen && (
        <FullscreenCloseButton onClick={() => setIsFullScreen(false)}>
          <CloseWhiteIcon />
        </FullscreenCloseButton>
      )}

      {goal.modelMesh ? (
        <MeshViewer
          measurementMesh={goal.modelMesh.measurementMesh}
          targetMesh={goal.modelMesh.targetMesh}
          isFullScreen={isFullscreen}
          setIsFullScreen={setIsFullScreen}
          displayedStartWeight={displayedStartWeight}
          displayedTargetWeight={displayedTargetWeight}
          goal={goal}
          zoomDistance={2.75}
        />
      ) : (
        <WeightTransition
          goal={goal}
          displayedStartWeight={displayedStartWeight}
          displayedTargetWeight={displayedTargetWeight}
        />
      )}

      <YourGoalContent
        style={{
          transform: isFullscreen ? "translateY(30px)" : "",
        }}
      >
        {goal && goal.newCaloriesPerDay !== null && (
          <WeightChangedNotification
            caloriesPerDay={goal.caloriesPerDay}
            newCaloriesPerDay={goal.newCaloriesPerDay}
          />
        )}

        <YourDailyTarget goal={goal} />
      </YourGoalContent>

      <FixCtaBox
        style={{
          zIndex: isFullscreen ? 0 : 100,
          pointerEvents: isFullscreen ? "none" : "auto",
          opacity: isFullscreen ? 0 : 1,
          transition: `all ${isFullscreen ? 0.1 : 0.2}s linear`,
        }}
      >
        <Link
          to={PATHS.app.yourGoal.updateBodyAndGoal}
          style={{ display: "block", width: "100%", position: "relative" }}
        >
          <Button
            $theme="dark"
            $borderLess
            style={{ width: "100%" }}
            $type="primary"
          >
            Change Goal
          </Button>
        </Link>
      </FixCtaBox>
    </YourGoalWrapper>
  )
}

export default YourGoal
